import { gql } from '@apollo/client';
import type { LanguageCodes } from '@onevesthq/ov-enums';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
} from '../assets/i18n/config';
import type { Organization } from './organization';
import type { TranslatedString } from './shared';

export const DISALLOWED_CHARS_REGEX = /[<>\\|^*`~]/;

export enum CustomFieldFormats {
  TOGGLE_SWITCH = 'TOGGLE_SWITCH',
  CHECKBOX = 'CHECKBOX',
  RADIO_BUTTON = 'RADIO_BUTTON',
  DROPDOWN_LIST = 'DROPDOWN_LIST',
  SEARCH_LIST = 'SEARCH_LIST',
  NUMBER = 'NUMBER',
  CURRENCY = 'CURRENCY',
  DATE = 'DATE',
  DATE_TIME = 'DATE_TIME',
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',
  TEXT_AREA = 'TEXT_AREA',
  SIMPLE_TEXT = 'SIMPLE_TEXT',
  PHYSICAL_ADDRESS = 'PHYSICAL_ADDRESS',
  CUSTOM_AGREEMENTS = 'CUSTOM_AGREEMENTS',
  BANK_ACCOUNT = 'BANK_ACCOUNT',
  CUSTOM = 'CUSTOM',
}

export enum CustomFieldTypes {
  TEXT = 'TEXT',
  INTEGER = 'INTEGER',
  DECIMAL = 'DECIMAL',
  SINGLE_SELECT = 'SINGLE_SELECT',
  MULTIPLE_SELECT = 'MULTIPLE_SELECT',
  DATE = 'DATE',
  BOOLEAN = 'BOOLEAN',
  CUSTOM_OBJECT = 'CUSTOM_OBJECT',
}

export enum CustomFieldStates {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum CustomFieldWorkflowSteps {
  PERSONAL_INFORMATION = 'PERSONAL_INFORMATION',
  PERSONS_OF_INTEREST = 'PERSONS_OF_INTEREST',
  NON_INDIVIDUAL_INFORMATION = 'NON_INDIVIDUAL_INFORMATION',
  EMPLOYMENT_INFORMATION = 'EMPLOYMENT_INFORMATION',
  APPROVAL = 'APPROVAL',
  DOCUMENTS = 'DOCUMENTS',
  EDIT_GOAL = 'EDIT_GOAL',
  EDIT_ACCOUNT = 'EDIT_ACCOUNT',
  HOUSEHOLD = 'HOUSEHOLD',
  RELATIONSHIP_INFORMATION = 'RELATIONSHIP_INFORMATION',
  RESIDENCY_INFORMATION = 'RESIDENCY_INFORMATION',
  INCOME_ASSETS_AND_DEBTS = 'INCOME_ASSETS_AND_DEBTS',
}

export enum CustomFieldObjectTypes {
  HOUSEHOLD = 'HOUSEHOLD',
  ENTITY = 'ENTITY',
  ACCOUNT = 'ACCOUNT',
  SUB_ACCOUNT = 'SUB_ACCOUNT',
  GOAL = 'GOAL',
  TRANSACTION = 'TRANSACTION',
  HOLDING = 'HOLDING',
  ACCOUNT_GROUP = 'ACCOUNT_GROUP',
}

export enum CustomFieldSource {
  CUSTODIAN = 'CUSTODIAN',
  INTERNAL = 'INTERNAL',
}

export type SelectOption = {
  displayText?: TranslatedString;
  value: string;
};

export enum CustomFieldConditionalRulesComparisonTypes {
  EQUALS = 'EQUALS',
  NOT_EQUALS = 'NOT_EQUALS',
  INCLUDES = 'INCLUDES',
  NOT_INCLUDES = 'NOT_INCLUDES',
  IN = 'IN',
  NOT_IN = 'NOT_IN',
  GREATER_THAN = 'GREATER_THAN',
  LESS_THAN = 'LESS_THAN',
  GREATER_THAN_AGO = 'GREATER_THAN_AGO',
  LESS_THAN_AGO = 'LESS_THAN_AGO',
}

export type CustomFieldConditionalRule = {
  field: string;
  comparison: CustomFieldConditionalRulesComparisonTypes;
  value: any;
};

export type CustomField<T = CustomFieldTypes> = {
  id?: string;
  format: CustomFieldFormats;
  organization: Organization;
  type: T;
  key: string;
  translatedDescription?: TranslatedString;
  translatedName?: TranslatedString;
  workflowStep?: CustomFieldWorkflowSteps;
  objectType: CustomFieldObjectTypes;
  selectOptions?: SelectOption[];
  additionalInfo?: any;
  externalField?: CustomFieldSource;
  conditionalRules?: CustomFieldConditionalRule[];
  isTriggerRulesEnabled?: boolean;
  isEnabled?: boolean;
  enablePositiveNegativeSign?: boolean;
  enableAggregation?: boolean;
};

export type CustomOption = {
  customField: CustomField;
  enabled: boolean;
  label: TranslatedString;
  required: string;
  additionalInfo?: any;
  position?: number;
};
export type CustomFieldValue<T = CustomFieldTypes> = {
  key: string;
  customField: CustomField<T>;
  selectedOptions?: SelectOption[];
  value?: T extends CustomFieldTypes.BOOLEAN
    ? boolean
    : T extends CustomFieldTypes.INTEGER
      ? number
      : T extends CustomFieldTypes.DECIMAL
        ? number
        : T extends CustomFieldTypes.DATE
          ? string
          : T extends CustomFieldTypes.SINGLE_SELECT
            ? string
            : T extends CustomFieldTypes.MULTIPLE_SELECT
              ? string[]
              : T extends CustomFieldTypes.CUSTOM_OBJECT
                ? any
                : string;
};

export const CUSTOM_FIELDS_FORMAT_MAP: {
  [key in CustomFieldTypes]: CustomFieldFormats[];
} = {
  [CustomFieldTypes.TEXT]: [
    CustomFieldFormats.SIMPLE_TEXT,
    CustomFieldFormats.TEXT_AREA,
    CustomFieldFormats.EMAIL,
    CustomFieldFormats.PHONE,
  ],
  [CustomFieldTypes.INTEGER]: [
    CustomFieldFormats.NUMBER,
    CustomFieldFormats.CURRENCY,
  ],
  [CustomFieldTypes.DECIMAL]: [
    CustomFieldFormats.NUMBER,
    CustomFieldFormats.CURRENCY,
  ],
  [CustomFieldTypes.SINGLE_SELECT]: [
    CustomFieldFormats.RADIO_BUTTON,
    CustomFieldFormats.DROPDOWN_LIST,
    CustomFieldFormats.SEARCH_LIST,
  ],
  [CustomFieldTypes.MULTIPLE_SELECT]: [
    CustomFieldFormats.CHECKBOX,
    CustomFieldFormats.DROPDOWN_LIST,
    CustomFieldFormats.SEARCH_LIST,
  ],
  [CustomFieldTypes.DATE]: [
    CustomFieldFormats.DATE,
    CustomFieldFormats.DATE_TIME,
  ],
  [CustomFieldTypes.CUSTOM_OBJECT]: [
    CustomFieldFormats.PHYSICAL_ADDRESS,
    CustomFieldFormats.CUSTOM_AGREEMENTS,
    CustomFieldFormats.BANK_ACCOUNT,
    CustomFieldFormats.CUSTOM,
  ],
  [CustomFieldTypes.BOOLEAN]: [
    CustomFieldFormats.TOGGLE_SWITCH,
    CustomFieldFormats.CHECKBOX,
  ],
};

export const CUSTOM_FIELDS_WORKFLOW_STEP_MAP: {
  [key in CustomFieldObjectTypes]: CustomFieldWorkflowSteps[];
} = {
  [CustomFieldObjectTypes.HOUSEHOLD]: [
    CustomFieldWorkflowSteps.HOUSEHOLD,
    CustomFieldWorkflowSteps.APPROVAL,
  ],
  [CustomFieldObjectTypes.ENTITY]: [
    CustomFieldWorkflowSteps.PERSONAL_INFORMATION,
    CustomFieldWorkflowSteps.PERSONS_OF_INTEREST,
    CustomFieldWorkflowSteps.NON_INDIVIDUAL_INFORMATION,
    CustomFieldWorkflowSteps.EMPLOYMENT_INFORMATION,
    CustomFieldWorkflowSteps.APPROVAL,
    CustomFieldWorkflowSteps.DOCUMENTS,
    CustomFieldWorkflowSteps.RELATIONSHIP_INFORMATION,
    CustomFieldWorkflowSteps.RESIDENCY_INFORMATION,
    CustomFieldWorkflowSteps.INCOME_ASSETS_AND_DEBTS,
  ],
  [CustomFieldObjectTypes.ACCOUNT]: [
    CustomFieldWorkflowSteps.EDIT_ACCOUNT,
    CustomFieldWorkflowSteps.APPROVAL,
  ],
  [CustomFieldObjectTypes.SUB_ACCOUNT]: [CustomFieldWorkflowSteps.APPROVAL],
  [CustomFieldObjectTypes.GOAL]: [
    CustomFieldWorkflowSteps.EDIT_GOAL,
    CustomFieldWorkflowSteps.APPROVAL,
  ],
  [CustomFieldObjectTypes.TRANSACTION]: [],
  [CustomFieldObjectTypes.HOLDING]: [],
  [CustomFieldObjectTypes.ACCOUNT_GROUP]: [],
};
export const CUSTOM_FIELDS_AVIALABLE_SUB_STEP = [
  CustomFieldWorkflowSteps.PERSONAL_INFORMATION,
  CustomFieldWorkflowSteps.PERSONS_OF_INTEREST,
  CustomFieldWorkflowSteps.NON_INDIVIDUAL_INFORMATION,
  CustomFieldWorkflowSteps.EMPLOYMENT_INFORMATION,
  CustomFieldWorkflowSteps.APPROVAL,
  CustomFieldWorkflowSteps.DOCUMENTS,
  CustomFieldWorkflowSteps.EDIT_ACCOUNT,
  CustomFieldWorkflowSteps.APPROVAL,
  CustomFieldWorkflowSteps.EDIT_GOAL,
  CustomFieldWorkflowSteps.HOUSEHOLD,
  CustomFieldWorkflowSteps.RELATIONSHIP_INFORMATION,
  CustomFieldWorkflowSteps.RESIDENCY_INFORMATION,
  CustomFieldWorkflowSteps.INCOME_ASSETS_AND_DEBTS,
];
export const FETCH_CUSTOM_FIELDS = gql`
query fetchCustomFields($input: FetchCustomFieldsInput!) {
  fetchCustomFields(input: $input) {
  customFields{
    id
    key
    type
    format
    objectType
    workflowStep
    translatedName{ ${TRANSLATED_STRING_FIELDS} }
    selectOptions{
      value
      displayText { ${TRANSLATED_STRING_FIELDS} }
    }
    translatedDescription { ${TRANSLATED_STRING_FIELDS} }
    externalField
    conditionalRules {
      field
      comparison
      value
    }
  }
}
}
`;

/**
 * Gets a display name for a custom field with fallback logic.
 * Returns translatedName in user's language, falls back to org's default language, then key, then id, or a fallback value.
 */
export const getCustomFieldDisplayName = (
  customField?: {
    translatedName?:
      | TranslatedString
      | Partial<Record<LanguageCodes, string | null | undefined>>
      | null;
    key?: string | null;
    id?: string | null;
  } | null,
  fallback = '',
): string => {
  if (!customField) return fallback;
  return (
    translateBackend(customField.translatedName) ||
    customField.key ||
    customField.id ||
    fallback
  );
};

/**
 * Collapses duplicate keys (last occurrence wins). Use after concatenating OV
 * and custodian arrays so live custodian values override stale Mongo copies.
 */
export function dedupeCustomFieldValuesByKeyLast(
  fields: CustomFieldValue[] | undefined | null,
): CustomFieldValue[] {
  if (!fields?.length) return [];
  const byKey = new Map<string, CustomFieldValue>();
  for (const f of fields) {
    byKey.set(f.key, f);
  }
  return Array.from(byKey.values());
}

export function mergeOvAndCustodianCustomFields(
  ovCustomFields: CustomFieldValue[],
  custodianCustomFields: CustomFieldValue[],
): CustomFieldValue[] {
  return dedupeCustomFieldValuesByKeyLast([
    ...ovCustomFields,
    ...custodianCustomFields,
  ]);
}
